import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  archiveDocumentStyleTemplate, createDocumentStyleTemplate, getListDocumentStyleTemplatesQueryKey, useListDocumentStyleTemplates,
  type DocumentStyleTemplate
} from '@cdf/api-client-react';
import { defaultDocumentStyles, validateDocumentStyles, type DocumentStyles } from '@cdf/api-zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getErrorMessage } from '@/lib/utils';

const PAGE = 10;

/**
 * Named style templates (Solution Studio's entity style sets). Applying a
 * template replaces this document's styles with a copy of the set; saving the
 * document's styles as a template shares them with the whole entity.
 */
export function DocumentStyleTemplates(props: {
  styles?: DocumentStyles; disabled: boolean; definitionsDisabled: boolean; onApply: (styles: DocumentStyles) => void;
}) {
  const [open, setOpen] = React.useState(false);
  return <>
    <Button type="button" size="sm" variant="ghost" disabled={props.disabled} onClick={() => setOpen(true)}>Style templates</Button>
    {open && <StyleTemplatesDialog {...props} onClose={() => setOpen(false)} />}
  </>;
}

function StyleTemplatesDialog({ styles, disabled, definitionsDisabled, onApply, onClose }: {
  styles?: DocumentStyles; disabled: boolean; definitionsDisabled: boolean; onApply: (styles: DocumentStyles) => void; onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [search, setSearch] = React.useState('');
  const [offset, setOffset] = React.useState(0);
  const [name, setName] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string>();
  const [message, setMessage] = React.useState<string>();
  const params = { search: search.trim() || undefined, limit: PAGE, offset };
  const list = useListDocumentStyleTemplates(params, { query: { queryKey: getListDocumentStyleTemplatesQueryKey(params) } });
  const page = list.data;
  const current = styles ?? defaultDocumentStyles();
  const refresh = () => queryClient.invalidateQueries({ queryKey: getListDocumentStyleTemplatesQueryKey() });

  const apply = (template: DocumentStyleTemplate) => {
    if (!window.confirm(`Replace this document's text styles with “${template.name}”? Text keeps its style names; matching styles take the template's look.`)) return;
    try {
      onApply(validateDocumentStyles({ schemaVersion: 1, definitions: template.definitions }));
      setError(undefined); setMessage(`Applied “${template.name}”. Save the document to keep it.`);
    } catch (cause) { setError(getErrorMessage(cause)); }
  };
  const save = async () => {
    setBusy(true); setError(undefined); setMessage(undefined);
    try {
      await createDocumentStyleTemplate({ name: name.trim(), definitions: current.definitions });
      setName(''); setMessage('Saved these styles as a template for your entity.'); await refresh();
    } catch (cause) { setError(getErrorMessage(cause)); } finally { setBusy(false); }
  };
  const archive = async (template: DocumentStyleTemplate) => {
    if (!window.confirm(`Archive “${template.name}”? Documents that already use it keep their styles.`)) return;
    setBusy(true); setError(undefined);
    try { await archiveDocumentStyleTemplate(template.id, { expectedRevision: template.revision }); setMessage(`Archived “${template.name}”.`); await refresh(); }
    catch (cause) { setError(getErrorMessage(cause)); } finally { setBusy(false); }
  };

  return <Dialog open onOpenChange={next => { if (!next) onClose(); }}>
    <DialogContent className="max-w-xl">
      <DialogHeader><DialogTitle>Style templates</DialogTitle>
        <DialogDescription>Complete sets of named text styles shared across your entity. Applying one copies it into this document.</DialogDescription></DialogHeader>
      <div className="space-y-4">
        <div className="space-y-1"><Label htmlFor="style-template-search">Search templates</Label>
          <Input id="style-template-search" maxLength={80} value={search} onChange={event => { setSearch(event.target.value); setOffset(0); }} /></div>
        {list.isError ? <p role="alert" className="text-sm text-destructive">Style templates could not be loaded.</p>
          : !page ? <p role="status">Loading style templates…</p>
          : page.items.length === 0 ? <p className="text-sm text-muted-foreground">No style templates{search.trim() ? ' match this search' : ' yet'}.</p>
          : <ul className="divide-y rounded-md border">{page.items.map(template => <li key={template.id} className="flex items-center justify-between gap-2 p-2">
              <div><p className="text-sm font-medium">{template.name}</p><p className="text-xs text-muted-foreground">{template.definitions.length} styles{template.description ? ` · ${template.description}` : ''}</p></div>
              <div className="flex gap-1">
                <Button type="button" size="sm" variant="outline" disabled={disabled || definitionsDisabled || busy} onClick={() => apply(template)}>Apply</Button>
                {page.canEdit && <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => void archive(template)}>Archive</Button>}
              </div>
            </li>)}</ul>}
        {page && page.total > PAGE && <div className="flex items-center justify-between text-sm">
          <Button type="button" size="sm" variant="outline" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</Button>
          <span>{offset + 1}–{Math.min(offset + PAGE, page.total)} of {page.total}</span>
          <Button type="button" size="sm" variant="outline" disabled={offset + PAGE >= page.total} onClick={() => setOffset(offset + PAGE)}>Next</Button>
        </div>}
        {page?.canEdit && <div className="space-y-1 border-t pt-3"><Label htmlFor="style-template-name">Save this document's styles as a template</Label>
          <div className="flex gap-2"><Input id="style-template-name" maxLength={80} value={name} onChange={event => setName(event.target.value)} placeholder="Template name" />
            <Button type="button" disabled={busy || !name.trim()} onClick={() => void save()}>Save template</Button></div></div>}
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        {message && <p role="status" className="text-sm">{message}</p>}
      </div>
    </DialogContent>
  </Dialog>;
}
